// useTawTransactions(twId, taws, teamScope, scope) -- the store-backed,
// PICKER-GATED replacement for src/hooks/useTawTransactions.js (40-08,
// COLD-02).
//
// TRUE state (do not repeat the old file's justification -- it is FALSE):
// `CappingStatusCard` does not exist anywhere in `src/` -- App.js:306's
// comment referencing it is stale. `IncomeV2Page.js:122` hard-codes
// `tawPipelines: []`, so nothing in the product actually consumes an
// always-on, all-TAW capping projection today. The only LIVE consumer is
// `AppRouter.js`'s `displayTransactions`, which already scope-filters by
// `teamScope` before rendering. There is therefore no reason to keep every
// TAW's transactions subscribed at boot regardless of what the picker shows,
// and gating them is provably lossless against the app's current behavior.
//
// Gating (D-01/D-02): `teamScope === 'own'` acquires ZERO slices, opens ZERO
// listeners, for any TAW count. A specific TAW id acquires exactly ONE slice.
// `teamScope === 'all'` acquires one slice PER TAW (Pattern 1, resolved by
// 40-RESEARCH.md -- a per-TAW slice maps directly onto all three gating
// states with no bespoke branch beyond "which subset of `taws` to iterate",
// unlike a single synthetic whole-fan-out slice, which `serializeScope`'s
// shape does not support and which would need custom invalidation whenever
// the TAW list changes mid-session). A stale `teamScope` value (a TAW id no
// longer present in `taws`) acquires zero slices rather than opening a
// listener for a TAW the user no longer has.
//
// Keying (D-27/D-28, locked by 40-03): each TAW's slice keys on the VIEWING
// workspace (segment 0 = `twId`, the TW), with the TAW id carried as the
// `@foreignId` marker -- `serializeScope(twId, 'transactions', scope, {
// foreignId: tawId })`. This is why `dropForeignWorkspace
// (selectedWorkspaceId)` (src/App.js) hard-evicts these slices on workspace
// switch with ZERO changes to that function -- its existing
// `${currentWorkspaceId}:` prefix check already matches this shape. The
// archive window (D-17) widens the key with `:archive:<year>` exactly the
// way `serializeScope` already appends it for the TW's own
// `useTransactions.js` -- e.g. `${twId}:@${tawId}:transactions:archive:2024`
// -- never a second, locally reimplemented key shape.
//
// Windowing (D-17 -- deliberate deviation from Phase 40 D-03): TAW queries
// are windowed by the caller-supplied `scope` (active by default, via the
// same shared `buildConstraints(scope, currentYear)` the TW's own
// `useTransactions.js` uses -- never a locally reimplemented derived-year
// clause). D-03 originally windowed every TAW query active-only, on the
// reasoning that the picker's list is a working list, not an archive
// browser. Phase 41 reopens that on purpose: a team owner reviewing an
// agent's closed deals for a selected archive year is a real need. The
// archive branch is a one-shot `getDocs` per `(taw, year)` slice, never a
// live listener (mirrors `useTransactions.js`'s own D-22 archive branch).
// The archive YEAR SELECTOR remains the single source of truth -- this
// `scope` only widens WHICH WORKSPACES that year covers, it does not add a
// second, independent year axis. "All archived years for the picked TAW"
// was explicitly rejected as unbounded and self-contradictory on screen (it
// would make the archive-year control and the TAW picker disagree about
// which year is showing).
//
// Index coverage (D-29): this file adds NO new `firestore.indexes.json`
// entry. The existing `transactions` collectionGroup index (derived-year
// range ASC, close-date ASC) already covers every TAW's `workspaces/
// {tawId}/transactions` path, because composite indexes are collection-ID
// scoped, not path scoped. A green mocked test is NOT evidence for that
// claim -- `tests/mocks/firebase-firestore.js` has no index planner. 40-13's
// Hosting preview channel is the required, non-optional evidence, and 41-08's
// gate must now exercise BOTH the active and archive windows (D-17/D-27).
//
// Return contract: a single flat array, concatenating every acquired TAW
// slice's transactions, matching the OLD hook's array-return shape so
// `App.js`/`AppRouter.js` need no shape change when 40-11 wires this in.
// Each transaction is enriched with `_workspaceId`, `_workspaceName`, and
// `_splitPercentToAgent` -- the exact three fields `AppRouter.js`'s
// `displayTransactions` reads (`_workspaceId` for the scope filter and the
// child-guard, `_splitPercentToAgent` for the parent-net-income derivation,
// `_workspaceName` for the "all" scope's TW-row name-blanking pairing).
// `src/hooks/useWorkspaceHandlers.js:241` strips all three before a write,
// so this enrichment must never leak into a save payload -- it never does,
// because this store is READ-ONLY (writes stay in transactionService.js).
// Both the active and archive branches share ONE enrichment function
// (`enrichTawRows`) so these three fields cannot drift between windows.
//
// Concatenation is memoized per hook instance (`cacheRef`): `getSnapshot`
// itself already returns a per-slice REFERENCE-STABLE array (Pitfall 3,
// workspaceStore.js's `computeSliceSnapshot` cache), but naively
// concatenating N such arrays on every call would still allocate a fresh
// top-level array each time even when nothing changed -- an allocating
// snapshot getter otherwise causes an infinite external-store re-render
// loop. The cache keyed on the sorted TAW-id list PLUS the window (`scopeKey`)
// plus each slice's own reference avoids that: unchanged inputs return the
// SAME concatenated array, and an active->archive window switch (same TAW-id
// set) cannot return the previous window's memoized concatenation.

import React from 'react';
import { collection, getDocs, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase.js';
import { acquire, release, subscribe, getSnapshot } from '../workspaceStore.js';
import { serializeScope } from '../sliceKeys.js';
import { buildConstraints } from '../queryConstraints.js';
import { mapTransactions } from '../../hooks/useWorkspaceData.js';
import { dropMigratedDeals } from '../../utils/migratedDeals.js';

const EMPTY_TAW_TRANSACTIONS = Object.freeze([]);
const EMPTY_TAW_IDS = Object.freeze([]);
const ACTIVE_SCOPE = { kind: 'active' };

/**
 * Reports a TAW slice failure distinctly from a silently empty list (D-17's
 * whole reason for existing -- a soft `permission-denied` presenting as "this
 * agent closed nothing" is the exact silent-failure pattern that hid Findings
 * A and B for months). This hook has no `useToast` access -- the store
 * builder is a plain module function, not a component -- so the console
 * channel is the surfacing mechanism, mirroring `useWorkspaceList.js:90-99`'s
 * idiom. Exported so a caller with toast access (or a test) can inject an
 * override via `onError`.
 */
export function reportTawSliceError(tawId, error) {
    const message = error?.code === 'permission-denied'
        ? `Permission denied reading team-agent workspace ${tawId} transactions. Check workspace sharing or security rules.`
        : `Could not load team-agent workspace ${tawId} transactions.`;
    console.error(message, error);
}

/**
 * Pure row enrichment shared by BOTH the active (onSnapshot) and archive
 * (getDocs) branches, so the three fields every consumer depends on --
 * `_workspaceId`, `_workspaceName`, `_splitPercentToAgent` -- can never drift
 * between windows. `mapTransactions` backfills a `clients` array from legacy
 * clientName/clientEmail/clientPhone fields for any TAW document that
 * predates the `clients` array shape -- the deleted
 * src/hooks/useTawTransactions.js applied this before enrichment and this
 * store-backed replacement must match that contract (WR-02, 40-REVIEW.md).
 *
 * Phase 54 D-02: BOTH branches below run `raw` through dropMigratedDeals
 * (src/utils/migratedDeals.js) BEFORE calling this function, so an archived,
 * migrated Prospect deal never reaches enrichment or a TAW picker consumer.
 * Client-side filter only -- see that file's header comment.
 */
export function enrichTawRows(rawDocs, tawId, taw) {
    return mapTransactions(rawDocs).map((t) => ({
        ...t,
        _workspaceId: tawId,
        _workspaceName: taw?.name || tawId,
        // TW's share = 100 - TAW's splitPercentToAgent (old hook's
        // contract, preserved verbatim).
        _splitPercentToAgent: taw?.splitPercentToAgent ?? 100,
    }));
}

/**
 * Builds the acquire()-shaped openListener closure for a single TAW's
 * transactions slice, windowed by `scope` (active by default). Exported
 * (alongside the hook) so the store's real refcount/grace-TTL machinery
 * (39-03) can drive this EXACT listener-opening code directly in tests,
 * mirroring src/store/hooks/useTransactions.js's identical convention --
 * this repo's DOM-less test runner cannot commit React effects through a
 * real fiber tree.
 *
 * `taw` (the { id, name, splitPercentToAgent } record from the `taws` array
 * at the moment this TAW's slice is FIRST acquired) supplies the enrichment
 * fields. Like every other lease in this store, `acquire()` only invokes
 * `openListener` on the refcount 0->1 transition -- a later change to the
 * TAW's name or split percentage that does not change the active TAW-id SET
 * does not reopen this listener, exactly the same acquire-time-capture
 * discipline `useTransactions.js`/`useLeasedCollection.js` already hold for
 * their own captured `workspaceId`/`collectionName` parameters.
 *
 * `scope.kind === 'archive'` mirrors `useTransactions.js:46-55` exactly: a
 * one-shot `getDocs` fetch, cached under its own slice, never a live
 * subscription. Both branches report a failure through `onError` distinctly
 * from a silently empty list.
 */
export function buildTawTransactionsListener(tawId, taw, currentYear, scope = ACTIVE_SCOPE, onError = reportTawSliceError) {
    return (setSliceData) => {
        const q = query(
            collection(db, 'workspaces', tawId, 'transactions'),
            ...buildConstraints(scope, currentYear),
        );

        if (scope.kind === 'archive') {
            // D-17/D-22: one-shot getDocs, cached under this archive:<year>
            // sliceKey -- NOT a live listener.
            getDocs(q).then((snapshot) => {
                const raw = dropMigratedDeals(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
                setSliceData(enrichTawRows(raw, tawId, taw));
            }).catch((error) => onError(tawId, error));
            return undefined;
        }

        return onSnapshot(q, (snapshot) => {
            const raw = dropMigratedDeals(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
            setSliceData(enrichTawRows(raw, tawId, taw));
        }, (error) => onError(tawId, error));
    };
}

// Pure predicate: which TAW ids should be ACTIVE (acquired) for the current
// (twId, taws, teamScope, scope) quadruple. A stale teamScope value -- a TAW
// id not present in the current `taws` array -- resolves to an empty set
// (T-40-08-02) so a stale picker value can never open a listener for a TAW
// the user no longer has. A malformed archive year (D-17) also resolves to
// an empty set, LOUDLY (via reportTawSliceError) rather than silently
// rendering an empty archive year that looks like "this agent closed
// nothing".
function computeActiveTawIds(twId, taws, teamScope, scope = ACTIVE_SCOPE) {
    if (!twId) return EMPTY_TAW_IDS;
    if (!Array.isArray(taws) || taws.length === 0) return EMPTY_TAW_IDS;
    if (teamScope === 'own') return EMPTY_TAW_IDS;
    if (scope?.kind === 'archive' && !Number.isFinite(Number(scope.year))) {
        reportTawSliceError(twId, new Error(`Malformed archive year for scope: ${JSON.stringify(scope)}`));
        return EMPTY_TAW_IDS;
    }
    if (teamScope === 'all') return taws.map((t) => t.id);
    return taws.some((t) => t.id === teamScope) ? [teamScope] : EMPTY_TAW_IDS;
}

// Effect-dedup key only (D-18) -- NEVER a slice key. A caller inlining a
// fresh `{ kind: 'archive', year }` object literal each render would
// otherwise churn a live lease on object identity alone; this primitive
// string lets the acquire/release effect and the sliceKeys memo dedupe on
// VALUE instead. Slice keys still come only from `serializeScope`.
function describeScope(scope) {
    return scope?.kind === 'archive' ? `archive:${scope.year}` : scope?.kind;
}

export function useTawTransactions(twId, taws, teamScope, scope = ACTIVE_SCOPE) {
    const activeTawIds = React.useMemo(
        () => computeActiveTawIds(twId, taws, teamScope, scope),
        [twId, taws, teamScope, scope],
    );

    // Sorted, joined id list: the canonical dedup key for the acquire/
    // release effect below, so a REORDER of `taws` (same id set, different
    // array order) never churns a still-live listener.
    const sortedTawIds = React.useMemo(() => [...activeTawIds].sort(), [activeTawIds]);
    const idsKey = sortedTawIds.join(',');
    const scopeKey = describeScope(scope);

    // The file's ONLY serializeScope call site (D-18) -- both the acquire/
    // release effect and the useSyncExternalStore selector below consume
    // this SAME memoized array, so an active<->archive window switch with an
    // identical TAW-id set re-keys under NEW slice keys instead of reusing
    // the previous window's cache.
    // idsKey and scopeKey are the canonical dedup keys for (twId,
    // active-TAW-set, window); `scope`/`sortedTawIds` are read inside for
    // their VALUE only, and value-equivalent inputs always resolve to the
    // identical scopeKey/idsKey pair.
    const sliceKeys = React.useMemo(
        () => sortedTawIds.map((tawId) => serializeScope(twId, 'transactions', scope, { foreignId: tawId })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [twId, idsKey, scopeKey],
    );

    React.useEffect(() => {
        if (!twId || sortedTawIds.length === 0) return undefined;

        const currentYear = new Date().getFullYear();
        const tawById = new Map((taws || []).map((t) => [t.id, t]));

        sliceKeys.forEach((sliceKey, i) => {
            const tawId = sortedTawIds[i];
            acquire(sliceKey, buildTawTransactionsListener(tawId, tawById.get(tawId), currentYear, scope));
        });

        return () => {
            sliceKeys.forEach((sliceKey) => release(sliceKey));
        };
        // idsKey/scopeKey (the canonical dedup keys for the active-TAW-set
        // and the window) drive re-acquisition: an unchanged pair implies an
        // equivalent (twId, active-TAW-set, scope) triple, exactly like
        // useTransactions.js's sliceKey discipline. `sliceKeys`/
        // `sortedTawIds` are derived from those keys and `taws`/`scope` are
        // read inside the closure only for acquire-time enrichment/query
        // metadata (see buildTawTransactionsListener's docblock) --
        // deliberately excluded so a TAW's name/split changing alone, or a
        // fresh-but-equivalent scope object literal, cannot churn a
        // still-active listener.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [twId, idsKey, scopeKey]);

    const cacheRef = React.useRef({ cacheKey: null, entries: [], result: EMPTY_TAW_TRANSACTIONS });

    return React.useSyncExternalStore(
        subscribe,
        () => {
            if (!twId || sortedTawIds.length === 0) return EMPTY_TAW_TRANSACTIONS;

            const cacheKey = [idsKey, scopeKey].join('::');
            const currentEntries = sliceKeys.map((sliceKey) => getSnapshot(sliceKey));

            const cache = cacheRef.current;
            const unchanged = cache.cacheKey === cacheKey
                && cache.entries.length === currentEntries.length
                && cache.entries.every((entry, i) => entry === currentEntries[i]);
            if (unchanged) return cache.result;

            const merged = [];
            currentEntries.forEach((entry) => {
                if (entry) merged.push(...entry);
            });
            const result = merged.length > 0 ? merged : EMPTY_TAW_TRANSACTIONS;
            cacheRef.current = { cacheKey, entries: currentEntries, result };
            return result;
        },
        // getServerSnapshot: same selector shape, reused verbatim -- the
        // useTransactions.js/useLeasedCollection.js WR-02 precedent. Keeps
        // this hook SSR-safe (renderToStaticMarkup smoke tests).
        () => EMPTY_TAW_TRANSACTIONS,
    );
}
